import React, { useCallback, useEffect, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import NiceModal from "@ebay/nice-modal-react";
import { useAppBaseContext, useConfiguratorPopupContext } from "providers";
import { UncontrolledTooltip } from "reactstrap";
import { useTranslate } from "hooks";
import { useAuthStore } from "stores/authStore";
import { isWirelessTenantAllowed } from "utils";
import { Icon } from "../../Icon/Icon";
import { LinkButtonWithChevron } from "../../LinkButtonWithChevron";
import { LeftSidebarSubNavHeader } from "../LeftSidebarSubNavHeader";
import { AddWizardToProject } from "../../_Modals/AddWizardToProject";
import { useEnabledWizardObjs } from "hooks/wizardHooks/useEnabledWizardObjs";

import s from "../LeftSidebar.module.scss";
import { useProjectDataStore } from "stores/projectStore";

export const LeftSidebarWizardsSubnavFirst = Scrivito.connect(
  ({ references: [wizardListObjRef, wizardObjRef], setActiveButton }) => {
    const translate = useTranslate();
    const projectId = useProjectDataStore((state) => state.projectId);
    const { subType } = Scrivito.currentPageParams();
    const [isWizardListItemActive, setIsWizardListItemActive] = useState(false);
    const { isProd } = useAppBaseContext();
    const { isUpdateButtonActive, showConfiguratorPopup } =
      useConfiguratorPopupContext();
    const { wizardObjs, isLoading } = useEnabledWizardObjs();

    const currentUserTenant = useAuthStore.use.currentUser()?.tenant;

    useEffect(
      () =>
        setIsWizardListItemActive(
          Scrivito.currentPage().objClass() === "WizardList",
        ),
      [subType],
    );

    const navigateToWizardPage = (wizardSubtype) => {
      const isWizardPage = window.location.href.includes("wizard");
      setActiveButton(null);
      if (isWizardPage) {
        const query = `subType=${wizardSubtype}&projectId=${projectId}&step=0`;
        const url = Scrivito.urlFor(wizardObjRef, {
          query,
        });
        window.location.href = url;
      } else {
        Scrivito.navigateTo(() => wizardObjRef, {
          params: {
            subType: wizardSubtype,
            projectId,
            step: "0",
          },
        });
      }
    };

    const wizardListNav = useCallback(() => {
      setActiveButton(null);
      Scrivito.navigateTo(wizardListObjRef, {
        params: { projectId },
      });
    }, [projectId, wizardListObjRef, setActiveButton]);

    const navigateToWizardList = useCallback(() => {
      if (isUpdateButtonActive) {
        showConfiguratorPopup(wizardListNav);
      } else {
        wizardListNav();
      }
    }, [isUpdateButtonActive, showConfiguratorPopup, wizardListNav]);

    if (isLoading) {
      return null;
    }

    return (
      <>
        <LinkButtonWithChevron
          onClick={navigateToWizardList}
          className={cx(s.leftSidebarSubNavLink, {
            [s.leftSidebarSubNavLinkActive]: isWizardListItemActive,
          })}
        >
          <LeftSidebarSubNavHeader
            referenceObj={wizardListObjRef}
            title={translate("WIZARDS_OVERVIEW_TITLE")}
            subtitle={translate("WIZARDS_OVERVIEW_SUBTITLE")}
          />
        </LinkButtonWithChevron>
        {wizardObjs?.map((wizard) => {
          const isProdReady = wizard.get("isProdReady");
          if (!isProdReady && isProd) {
            return null;
          }
          const wizardSubtype = wizard.get("subType");
          if (
            wizardSubtype === "roomcontrolwireless" &&
            !isWirelessTenantAllowed(currentUserTenant)
          ) {
            return null;
          }
          const linkForMenuItem = wizard.get("linkForMenuItem");
          const wizardTitle = translate.stringOnly(
            `WIZARDS_OVERVIEW_${wizardSubtype}_TITLE`,
            null,
          );
          const wizardSubtitle = translate(
            `WIZARDS_LEFTSIDEBAR_MENU_${wizardSubtype}_SUBTITLE`,
          );

          const navigateToWizard = () => {
            if (linkForMenuItem) {
              window.open(linkForMenuItem.url(), "_blank").focus();
            } else if (!projectId) {
              NiceModal.show(AddWizardToProject, {
                wizardItem: wizard,
                setActiveButton,
              });
            } else {
              navigateToWizardPage(wizardSubtype);
            }
          };
          return (
            <button
              key={wizardSubtype}
              className={cx(
                "px-4 py-3 d-flex justify-content-between align-items-center gap-2 text-nowrap text-decoration-none cursor-pointer",
                s.leftSidebarSubNavLink,
                {
                  [s.leftSidebarSubNavLinkActive]: subType === wizardSubtype,
                },
              )}
              onClick={() => {
                if (isUpdateButtonActive) {
                  showConfiguratorPopup(navigateToWizard);
                  return;
                }
                navigateToWizard();
              }}
            >
              <div
                className="d-flex flex-column gap-1 align-items-start"
                id={wizardTitle.replace(/[^a-zA-Z0-9]/g, "")}
              >
                <h4 className={cx("title mb-0", s.leftSidebarSubNavLinkTitle)}>
                  {wizardTitle}
                </h4>
                <span
                  className={cx("fw-normal", s.leftSidebarSubNavLinkSubtitle)}
                >
                  {wizardSubtitle}
                </span>
              </div>
              <UncontrolledTooltip
                target={wizardTitle.replace(/[^a-zA-Z0-9]/g, "")}
                placement="top"
              >
                {wizardTitle} <br />
                {wizardSubtitle}
              </UncontrolledTooltip>
              <Icon
                className="d-flex align-items-center"
                prefix="trox"
                iconName="chevron-right-regular"
              />
            </button>
          );
        })}
      </>
    );
  },
);
